import { useMemo } from "react";
import {
  Box,
  Flex,
  Tag,
  VStack,
  Icon,
  Text,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { createIcon } from "@chakra-ui/icons";
import { useHookstate } from "@hookstate/core";
import { appStore } from "../../../../hookstate/appStore";
import { useSearchBrowseSections } from "../../hooks/useSearchBrowseSections";
import { SectionHeader } from "./SectionHeader";
import { ContentCard } from "../../../../components/cards/ContentCard";
import { ButtonCarousel } from "../../../../components/carousel/ButtonCarousel";
import { sizeEstimator } from "../../../../components/cards/hooks/useGridCardDimensions";
import type { Section } from "../../../../types";

const DYNAMIC_SOURCE_TYPES = [
  "dynamic-comics",
  "dynamic-creators",
  "dynamic-releases",
  "dynamic-bundles",
];

function getItems(section: Section) {
  switch (section.meta.layout.style.context) {
    case "comics":
      return section.comics ?? [];
    case "creators":
      return section.artists ?? [];
    case "releases":
      return section.releases ?? [];
    case "bundle":
      return section.bundles ?? [];
    default:
      return [];
  }
}

// ========================================================================
// Tag Icons — semantic icons per type/genre (same as admin preview)
// ========================================================================

const ComicBookIcon = createIcon({
  displayName: "ComicBookIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M19 2H6c-1.206 0-3 .799-3 3v14c0 2.201 1.794 3 3 3h15v-2H6.012C5.55 19.988 5 19.806 5 19s.55-.988 1.012-1H21V4c0-1.103-.897-2-2-2zm0 14H5V5c0-.806.55-.988 1-1h13v12z"
    />
  ),
});

const GraphicNovelIcon = createIcon({
  displayName: "GraphicNovelIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M6.012 18H21V4c0-1.103-.897-2-2-2H6c-1.206 0-3 .799-3 3v14c0 2.201 1.794 3 3 3h15v-2H6.012C5.55 19.988 5 19.806 5 19s.55-.988 1.012-1zM8 6h9v2H8V6zm0 4h9v2H8v-2z"
    />
  ),
});

const MangaIcon = createIcon({
  displayName: "MangaIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M21.947 9.179a1.001 1.001 0 00-.868-.676l-5.701-.453-2.467-5.461a.998.998 0 00-1.822-.001L8.622 8.05l-5.701.453a1 1 0 00-.619 1.713l4.213 4.107-1.49 6.452a1 1 0 001.53 1.057L12 18.202l5.445 3.63a1.001 1.001 0 001.517-1.106l-1.829-6.4 4.536-4.082c.297-.268.406-.686.278-1.065z"
    />
  ),
});

const WebcomicIcon = createIcon({
  displayName: "WebcomicIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm7.931 9h-2.764a14.67 14.67 0 00-1.792-6.243A8.013 8.013 0 0119.931 11zM12.53 4.027c1.035 1.364 2.427 3.78 2.627 6.973H8.843c.2-3.193 1.592-5.61 2.627-6.973.164-.006.328-.027.497-.027h.066c.169 0 .333.021.497.027zM8.625 4.757A14.67 14.67 0 006.833 11H4.069a8.013 8.013 0 014.556-6.243zM4.069 13h2.764a14.67 14.67 0 001.792 6.243A8.013 8.013 0 014.069 13zm8.461 6.973c-1.035-1.364-2.427-3.78-2.627-6.973h6.314c-.2 3.193-1.592 5.61-2.627 6.973-.164.006-.328.027-.497.027h-.066a7.033 7.033 0 01-.497-.027zm2.845-.73A14.67 14.67 0 0017.167 13h2.764a8.013 8.013 0 01-4.556 6.243z"
    />
  ),
});

// Genre icons
const ActionIcon = createIcon({
  displayName: "ActionIcon",
  viewBox: "0 0 24 24",
  path: <path fill="currentColor" d="M7 2v11h3v9l7-12h-4l4-8z" />,
});
const RomanceIcon = createIcon({
  displayName: "RomanceIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
    />
  ),
});
const ComedyIcon = createIcon({
  displayName: "ComedyIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8zm-5-6h10c0 2.757-2.243 5-5 5s-5-2.243-5-5zm3-4a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm4 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z"
    />
  ),
});
const SciFiIcon = createIcon({
  displayName: "SciFiIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M12 2a7 7 0 00-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 001 1h6a1 1 0 001-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 00-7-7zm2 15h-4v-1h4v1zm1.1-3.75l-.6.42V15h-5v-1.33l-.6-.42A5 5 0 017 9a5 5 0 0110 0 4.98 4.98 0 01-1.9 3.93V13.25zM9 20h6v1a1 1 0 01-1 1h-4a1 1 0 01-1-1v-1z"
    />
  ),
});
const HistoryIcon = createIcon({
  displayName: "HistoryIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8zm1-13h-2v6l5.25 3.15.75-1.23-4-2.42V7z"
    />
  ),
});
const HorrorIcon = createIcon({
  displayName: "HorrorIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M12 2a9 9 0 00-9 9c0 4.17 2.84 7.67 6.69 8.69a.5.5 0 00.62-.47V17.5a.5.5 0 00-.16-.37A6.97 6.97 0 015 11a7 7 0 0114 0c0 2.54-1.36 4.77-3.4 6l-.1.06V8.5a.5.5 0 00-1 0v2a.5.5 0 01-1 0v-3a.5.5 0 00-1 0v3a.5.5 0 01-1 0v-2a.5.5 0 00-1 0v10.69a.5.5 0 00.62.47A9 9 0 0012 2z"
    />
  ),
});
const AdventureIcon = createIcon({
  displayName: "AdventureIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"
    />
  ),
});
const TeenIcon = createIcon({
  displayName: "TeenIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M12 3c-4.97 0-9 4.03-9 9v7c0 1.1.9 2 2 2h2v-8H5v-1c0-3.87 3.13-7 7-7s7 3.13 7 7v1h-2v8h2c1.1 0 2-.9 2-2v-7c0-4.97-4.03-9-9-9z"
    />
  ),
});
const FantasyIcon = createIcon({
  displayName: "FantasyIcon",
  viewBox: "0 0 24 24",
  path: (
    <>
      <path
        fill="currentColor"
        d="M11.5 2l1.09 3.41L16 6.5l-3.41 1.09L11.5 11l-1.09-3.41L7 6.5l3.41-1.09L11.5 2z"
      />
      <path
        fill="currentColor"
        d="M18 7l.63 1.92L20.56 9.56 18.63 10.19 18 12.11l-.63-1.92L15.44 9.56 17.37 8.92 18 7z"
      />
      <rect
        fill="currentColor"
        x="2.29"
        y="13.09"
        width="16"
        height="2.8"
        rx="1.4"
        transform="rotate(-45 10.29 14.49)"
      />
    </>
  ),
});
const GraduationIcon = createIcon({
  displayName: "GraduationIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"
    />
  ),
});
const CrimeIcon = createIcon({
  displayName: "CrimeIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"
    />
  ),
});
const DramaIcon = createIcon({
  displayName: "DramaIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M2 16.5C2 19.538 4.462 22 7.5 22s5.5-2.462 5.5-5.5c0-.177-.013-.352-.033-.524L7.81 10.839A5.514 5.514 0 002 16.5zM7.5 8C4.462 8 2 5.538 2 2.5 2 2.224 2.224 2 2.5 2s.5.224.5.5C3 5.262 5.238 7.5 8 7.5c.276 0 .5.224.5.5s-.224.5-.5.5c-.168 0-.335-.006-.5-.017zM16.5 2C13.462 2 11 4.462 11 7.5c0 .177.013.352.033.524l5.157 5.137A5.514 5.514 0 0022 7.5C22 4.462 19.538 2 16.5 2zM16.5 16c-2.762 0-5-2.238-5-5-.001-.276-.225-.5-.501-.5s-.499.224-.499.5c0 3.038 2.462 5.5 5.5 5.5.168 0 .335.006.5.017V16.5c0-.276.224-.5.5-.5s.5.224.5.5-.224.5-.5.5z"
    />
  ),
});
const ThrillerIcon = createIcon({
  displayName: "ThrillerIcon",
  viewBox: "0 0 24 24",
  path: (
    <>
      <circle
        cx="12"
        cy="12"
        r="7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <rect fill="currentColor" x="11" y="1" width="2" height="5" />
      <rect fill="currentColor" x="11" y="18" width="2" height="5" />
      <rect fill="currentColor" x="1" y="11" width="5" height="2" />
      <rect fill="currentColor" x="18" y="11" width="5" height="2" />
    </>
  ),
});
const SliceOfLifeIcon = createIcon({
  displayName: "SliceOfLifeIcon",
  viewBox: "0 0 24 24",
  path: (
    <>
      <path
        fill="currentColor"
        d="M12 7c-2.76 0-5 2.24-5 5h10c0-2.76-2.24-5-5-5z"
      />
      <rect fill="currentColor" x="11" y="1" width="2" height="3" />
      <rect
        fill="currentColor"
        x="18.36"
        y="4.22"
        width="2"
        height="3"
        transform="rotate(45 19.36 5.72)"
      />
      <rect
        fill="currentColor"
        x="2.64"
        y="4.22"
        width="2"
        height="3"
        transform="rotate(-45 3.64 5.72)"
      />
      <rect fill="currentColor" x="1" y="13" width="22" height="2" rx="1" />
      <path
        fill="currentColor"
        d="M2 17h2c1.1 0 2 .45 2.72 1.17C7.53 18.9 8.46 19.5 10 19.5c1.54 0 2.47-.6 3.28-1.33A3.75 3.75 0 0116 17h2a3.75 3.75 0 012.72 1.17C21.53 18.9 22 19.5 22 19.5v2s-.94-.6-2.28-1.33A3.75 3.75 0 0018 19h-2c-1.1 0-2 .45-2.72 1.17C12.47 20.9 11.54 21.5 10 21.5c-1.54 0-2.47-.6-3.28-1.33A3.75 3.75 0 004 19H2v-2z"
      />
    </>
  ),
});
const SportsIcon = createIcon({
  displayName: "SportsIcon",
  viewBox: "0 0 24 24",
  path: (
    <>
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
      />
      <path
        fill="currentColor"
        d="M12 4c-1.95 0-3.72.71-5.1 1.87C7.86 8.28 9.8 10 12 10s4.14-1.72 5.1-4.13A7.93 7.93 0 0012 4z"
        opacity="0.6"
      />
      <path
        fill="currentColor"
        d="M4.34 9.44A8.03 8.03 0 004 12c0 2.84 1.49 5.33 3.72 6.74.12-2.89 1.6-5.42 3.8-6.96A7.98 7.98 0 014.34 9.44z"
        opacity="0.6"
      />
      <path
        fill="currentColor"
        d="M12.48 11.78c2.2 1.54 3.68 4.07 3.8 6.96A7.97 7.97 0 0020 12c0-.88-.14-1.73-.34-2.56a7.98 7.98 0 01-7.18 2.34z"
        opacity="0.6"
      />
    </>
  ),
});
const PeopleIcon = createIcon({
  displayName: "PeopleIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
    />
  ),
});
const ChildIcon = createIcon({
  displayName: "ChildIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
    />
  ),
});
const TagIcon = createIcon({
  displayName: "TagIcon",
  viewBox: "0 0 24 24",
  path: (
    <path
      fill="currentColor"
      d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z"
    />
  ),
});

// ── Icon + color mapping ────────────────────────────────────────────────

type TagMeta = { icon: ReturnType<typeof createIcon>; color: string };

const TYPE_TAG_META: Record<string, TagMeta> = {
  "1": { icon: ComicBookIcon, color: "#4299E1" },
  "2": { icon: GraphicNovelIcon, color: "#9F7AEA" },
  "3": { icon: MangaIcon, color: "#F56565" },
  "4": { icon: WebcomicIcon, color: "#48BB78" },
};

const GENRE_TAG_META: Record<string, TagMeta> = {
  "1": { icon: ActionIcon, color: "#F56565" },
  "2": { icon: RomanceIcon, color: "#ED64A6" },
  "3": { icon: ComedyIcon, color: "#F6E05E" },
  "4": { icon: SciFiIcon, color: "#4299E1" },
  "5": { icon: HistoryIcon, color: "#DD6B20" },
  "6": { icon: TeenIcon, color: "#38B2AC" },
  "7": { icon: FantasyIcon, color: "#9F7AEA" },
  "8": { icon: GraduationIcon, color: "#4FD1C5" },
  "9": { icon: HorrorIcon, color: "#718096" },
  "10": { icon: ChildIcon, color: "#FC8181" },
  "19": { icon: AdventureIcon, color: "#48BB78" },
  "20": { icon: TagIcon, color: "#B794F4" },
  "21": { icon: CrimeIcon, color: "#A0AEC0" },
  "22": { icon: ComedyIcon, color: "#FBBF24" },
  "23": { icon: ComedyIcon, color: "#F97316" },
  "24": { icon: SliceOfLifeIcon, color: "#68D391" },
  "25": { icon: SportsIcon, color: "#63B3ED" },
  "26": { icon: ThrillerIcon, color: "#E53E3E" },
  "27": { icon: TagIcon, color: "#4A5568" },
  "28": { icon: TagIcon, color: "#805AD5" },
  "29": { icon: PeopleIcon, color: "#D69E2E" },
  "30": { icon: DramaIcon, color: "#C05621" },
  "31": { icon: TagIcon, color: "#2D3748" },
};

const DEFAULT_TAG_META: TagMeta = { icon: TagIcon, color: "#A0AEC0" };

function getTagMeta(id: string | number, isTypeTags: boolean): TagMeta {
  const key = String(id);
  return (isTypeTags ? TYPE_TAG_META : GENRE_TAG_META)[key] ?? DEFAULT_TAG_META;
}

// ── Size configs ────────────────────────────────────────────────────────

interface TagSizeConfig {
  px: string;
  py: string;
  fontSize: string;
  gap: number;
  iconSize: number;
  showIcon: boolean;
  isCard: boolean;
  cardW: string;
  cardH: string;
}

const TAG_SIZE_CONFIGS: Record<string, TagSizeConfig> = {
  xs: {
    px: "8px",
    py: "5px",
    fontSize: "12px",
    gap: 1.5,
    iconSize: 0,
    showIcon: false,
    isCard: false,
    cardW: "0",
    cardH: "0",
  },
  s: {
    px: "12px",
    py: "8px",
    fontSize: "14px",
    gap: 2,
    iconSize: 0,
    showIcon: false,
    isCard: false,
    cardW: "0",
    cardH: "0",
  },
  m: {
    px: "14px",
    py: "10px",
    fontSize: "15px",
    gap: 2.5,
    iconSize: 16,
    showIcon: true,
    isCard: false,
    cardW: "0",
    cardH: "0",
  },
  l: {
    px: "0",
    py: "0",
    fontSize: "13px",
    gap: 3,
    iconSize: 28,
    showIcon: true,
    isCard: true,
    cardW: "100px",
    cardH: "90px",
  },
};

// ========================================================================
// FocusContent
// ========================================================================

interface FocusContentProps {
  cmsContext: string;
  onNavigate: (path: string) => void;
}

export function FocusContent({ cmsContext, onNavigate }: FocusContentProps) {
  const { data: sections, isLoading } = useSearchBrowseSections(cmsContext);

  const init = useHookstate(appStore.init);
  const comicTypes = init.comic_types.get({ noproxy: true });
  const comicGenres = init.comic_genres.get({ noproxy: true });

  if (isLoading) {
    return (
      <Center py="48px">
        <Spinner size="lg" color="blaze.blaze" />
      </Center>
    );
  }

  const hasSections = sections && sections.length > 0;

  if (!hasSections) return null;

  const renderSection = (section: Section) => {
    switch (section.source_type) {
      case "browse-type-tags":
        return (
          <BrowseTagsSection
            key={section.source_type}
            section={section}
            items={comicTypes ?? []}
            isTypeTags
            onNavigate={onNavigate}
          />
        );

      case "browse-genre-tags":
        return (
          <BrowseTagsSection
            key={section.source_type}
            section={section}
            items={comicGenres ?? []}
            isTypeTags={false}
            onNavigate={onNavigate}
          />
        );

      default: {
        if (!DYNAMIC_SOURCE_TYPES.includes(section.source_type)) return null;

        const items = getItems(section);
        if (!items.length) return null;

        const { context, mode, size } = section.meta.layout.style;
        const { width: cardWidth } = sizeEstimator(context, mode, size);
        const showViewAll = section.meta.layout.display?.include_view_all;
        return (
          <Flex direction="column" gap="24px" key={section.source_type}>
            <SectionHeader
              title={section.display_name}
              viewAllLabel={showViewAll ? "View all >" : undefined}
              onViewAll={
                showViewAll
                  ? () => onNavigate(`/cms/${section.uuid}`)
                  : undefined
              }
            />
            <ButtonCarousel
              width="100%"
              containerProps={{ gap: 3 }}
              includeButtons
              alwaysShowButtons
              buttonBg="transparent"
            >
              {items.map((item, index) => (
                <Box
                  key={item.id}
                  flexShrink={0}
                  w={
                    mode === "grid" && cardWidth ? `${cardWidth}px` : undefined
                  }
                >
                  <ContentCard
                    content={item}
                    layout={section.meta.layout}
                    section={section}
                    index={index}
                    area="search"
                  />
                </Box>
              ))}
            </ButtonCarousel>
          </Flex>
        );
      }
    }
  };

  return (
    <Flex direction="column" gap="32px" py="32px">
      {sections?.map((section: Section, i: number) => {
        const content = renderSection(section);
        if (!content) return null;
        return (
          <Flex direction="column" key={section.source_type} gap="32px">
            {i > 0 && <Box h="1px" bg="transparent.white.10" />}
            {content}
          </Flex>
        );
      })}
    </Flex>
  );
}

// ========================================================================
// BrowseTagsSection — reads layout mode, size, and filter config from section
// ========================================================================

interface TagItem {
  id: number | string;
  name: string;
  slug: string;
}

interface BrowseTagsSectionProps {
  section: Section;
  items: readonly TagItem[];
  isTypeTags: boolean;
  onNavigate: (path: string) => void;
}

function BrowseTagsSection({
  section,
  items,
  isTypeTags,
  onNavigate,
}: BrowseTagsSectionProps) {
  const init = useHookstate(appStore.init);
  const comicTypes = init.comic_types.get({ noproxy: true });
  const comicGenres = init.comic_genres.get({ noproxy: true });

  const layoutMode = section.meta.layout.style.mode || "horizontal";
  const sizeKey = section.meta.layout.style.size || "s";
  const cfg = TAG_SIZE_CONFIGS[sizeKey] ?? TAG_SIZE_CONFIGS.s;

  // Filter out OFF IDs from the section's filter config
  const offIds: Set<string> = useMemo(() => {
    const filterKey = isTypeTags ? "comic_type_id" : "comic_category_id";
    const raw = section.meta.filters?.limit?.[filterKey];
    if (!raw || !Array.isArray(raw)) return new Set<string>();
    return new Set(raw.map(String));
  }, [section.meta.filters, isTypeTags]);

  const visibleItems = useMemo(
    () => items.filter((item) => !offIds.has(String(item.id))),
    [items, offIds],
  );

  // ── Scope filters → build URL prefix + query params ──────────────────
  const scope = (section.meta.filters?.limit as Record<string, unknown>)
    ?.tags_scope as
    | {
        destination?: string;
        comic_type_id?: string;
        comic_category_id?: string;
        artist_type_id?: string;
        license_type_id?: string;
        comic_format_id?: string;
        artist_id?: string;
        comic_facets?: Record<string, string[]>;
      }
    | undefined;

  // Base route: /browse (default), /creators, /books, or /bundles
  const baseRoute =
    scope?.destination && scope.destination !== "browse"
      ? `/${scope.destination}`
      : "/browse";

  // Build slug lookup maps
  const typeSlugById = useMemo(() => {
    const map: Record<string, string> = {};
    comicTypes?.forEach((t) => {
      map[String(t.id)] = t.slug;
    });
    return map;
  }, [comicTypes]);

  const genreSlugById = useMemo(() => {
    const map: Record<string, string> = {};
    comicGenres?.forEach((g) => {
      map[String(g.id)] = g.slug;
    });
    return map;
  }, [comicGenres]);

  // Pre-compute the scope URL parts (shared across all tag chips)
  const { scopePrefix, scopeQuery } = useMemo(() => {
    let prefix = "";
    const params = new URLSearchParams();

    if (scope) {
      // For genre-tags: scope by type → /browse/{typeSlug}/{genreSlug}
      // For type-tags: scope by category → /browse/{typeSlug}/{categorySlug}
      if (isTypeTags && scope.comic_category_id) {
        const catSlug = genreSlugById[scope.comic_category_id];
        if (catSlug) prefix = catSlug;
      } else if (!isTypeTags && scope.comic_type_id) {
        const typeSlug = typeSlugById[scope.comic_type_id];
        if (typeSlug) prefix = typeSlug;
      }

      // Owner type → artist_type_id param
      if (scope.artist_type_id) {
        params.set("artist_type_id", scope.artist_type_id);
      }

      // Paywall / license type → license_type_id param
      if (scope.license_type_id) {
        params.set("license_type_id", scope.license_type_id);
      }

      // Format → comic_format_id param
      if (scope.comic_format_id) {
        params.set("comic_format_id", scope.comic_format_id);
      }

      // Artist → artist_id param
      if (scope.artist_id) {
        params.set("artist_id", scope.artist_id);
      }

      // Facets → query params
      if (scope.comic_facets) {
        Object.entries(scope.comic_facets).forEach(([groupId, optionIds]) => {
          optionIds?.forEach((optId) => {
            params.append(`facets[${groupId}][]`, optId);
          });
        });
      }
    }

    const qs = params.toString();
    return { scopePrefix: prefix, scopeQuery: qs ? `?${qs}` : "" };
  }, [scope, isTypeTags, typeSlugById, genreSlugById]);

  if (visibleItems.length === 0) return null;

  const tagChips = visibleItems.map((item) => {
    const meta = getTagMeta(item.id, isTypeTags);

    // Build the navigate URL with scope
    let browsePath: string;
    if (isTypeTags) {
      // type-tags: tag slug is the type, scope adds category after
      browsePath = scopePrefix
        ? `${baseRoute}/${item.slug}/${scopePrefix}${scopeQuery}`
        : `${baseRoute}/${item.slug}${scopeQuery}`;
    } else {
      // genre-tags: tag slug is the genre, scope adds type before
      browsePath = scopePrefix
        ? `${baseRoute}/${scopePrefix}/${item.slug}${scopeQuery}`
        : `${baseRoute}/${item.slug}${scopeQuery}`;
    }

    const handleClick = () => onNavigate(browsePath);

    // Large = card mode
    if (cfg.isCard) {
      return (
        <VStack
          key={String(item.id)}
          w={cfg.cardW}
          h={cfg.cardH}
          bg="transparent.white.10"
          borderRadius="lg"
          justify="center"
          align="center"
          spacing={2}
          cursor="pointer"
          flexShrink={0}
          _hover={{ bg: "transparent.white.20" }}
          onClick={handleClick}
        >
          <Icon
            as={meta.icon}
            boxSize={`${cfg.iconSize}px`}
            color={meta.color}
          />
          <Text
            fontSize={cfg.fontSize}
            fontWeight="500"
            fontFamily="Roboto"
            color="neutral.100"
            textAlign="center"
            lineHeight="1.2"
            px={1}
            noOfLines={2}
          >
            {item.name}
          </Text>
        </VStack>
      );
    }

    // Tiny, Small, Medium = pill mode
    return (
      <Tag
        key={String(item.id)}
        bg="transparent.white.10"
        borderRadius="full"
        px={cfg.px}
        py={cfg.py}
        fontSize={cfg.fontSize}
        fontWeight="500"
        fontFamily="Roboto"
        color="neutral.100"
        letterSpacing="0.5px"
        cursor="pointer"
        flexShrink={0}
        _hover={{ bg: "transparent.white.20" }}
        onClick={handleClick}
      >
        {cfg.showIcon && (
          <Icon
            as={meta.icon}
            boxSize={`${cfg.iconSize}px`}
            color={meta.color}
            mr="6px"
          />
        )}
        {item.name}
      </Tag>
    );
  });

  return (
    <Flex direction="column" gap="24px">
      <SectionHeader title={section.display_name} />

      {layoutMode === "list" ? (
        <Flex gap={cfg.gap} flexWrap="wrap">
          {tagChips}
        </Flex>
      ) : (
        <ButtonCarousel
          width="100%"
          containerProps={{ gap: cfg.gap }}
          alwaysShowButtons
          buttonBg="transparent"
          slidesToScroll={4}
        >
          {tagChips}
        </ButtonCarousel>
      )}
    </Flex>
  );
}
